import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { PricingTier } from "./PricingSection";

interface NicheModalProps {
  open: boolean;
  onClose: () => void;
  tier: PricingTier | null;
}

const INDUSTRY_KEYS = [
  "saas",
  "ecommerce",
  "realestate",
  "restaurants",
  "professional",
  "healthcare",
  "education",
  "creative",
  "nonprofit",
  "other",
] as const;

export default function NicheModal({ open, onClose, tier }: NicheModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rtl = i18n.language === "he";

  const [niche, setNiche] = useState("");
  const [industry, setIndustry] = useState("");
  const [industryOther, setIndustryOther] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setTimeout(() => {
        setNiche("");
        setIndustry("");
        setIndustryOther("");
        setErrorMsg("");
        setLoading(false);
      }, 350);
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tier) return;
    setLoading(true);
    setErrorMsg("");

    const resolvedIndustry = industry === "other" ? industryOther.trim() : industry
      ? t(`dyna.niche_modal.industries.${industry}`)
      : null;

    const resolvedNiche = niche.trim() || resolvedIndustry || null;

    try {
      await supabase.from("leads").insert({
        niche: resolvedNiche,
        preferred_tier: tier,
        source: "pricing_niche_modal",
        status: "new",
        language: rtl ? "he" : "en",
      });
    } catch (err) {
      // Log but don't block — BookPage will re-capture the lead with full details.
      // eslint-disable-next-line no-console
      console.error("[NicheModal] lead insert error:", err);
    }

    const params = new URLSearchParams({ tier });
    if (resolvedNiche) params.set("niche", resolvedNiche);

    setLoading(false);
    onClose();
    navigate(`/book?${params.toString()}`);
  };

  const tierName = tier ? t(`dyna.pricing.${tier}_name`) : "";

  return (
    <AnimatePresence>
      {open && tier && (
        <motion.div
          className="dyna-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.div
            className="dyna-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="niche-modal-title"
            initial={{ opacity: 0, scale: 0.94, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 16 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <button className="dyna-modal__close" onClick={onClose} aria-label="Close">✕</button>

            <h2 id="niche-modal-title">{t("dyna.niche_modal.title")}</h2>
            <p>{t("dyna.niche_modal.sub", { tier: tierName })}</p>

            <form className="dyna-modal__form" onSubmit={submit}>
              <div className="dyna-modal__field">
                <label className="dyna-modal__label" htmlFor="niche-input">
                  {t("dyna.niche_modal.niche_label")}
                </label>
                <input
                  id="niche-input"
                  className="dyna-modal__input"
                  type="text"
                  autoFocus
                  placeholder={t("dyna.niche_modal.niche_ph")}
                  value={niche}
                  onChange={(e) => setNiche(e.target.value)}
                />
              </div>

              <div className="dyna-modal__field">
                <label className="dyna-modal__label" htmlFor="niche-industry">
                  {t("dyna.niche_modal.industry_label")}
                </label>
                <select
                  id="niche-industry"
                  className="dyna-modal__input"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <option value="">—</option>
                  {INDUSTRY_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {t(`dyna.niche_modal.industries.${k}`)}
                    </option>
                  ))}
                </select>
              </div>

              {industry === "other" && (
                <div className="dyna-modal__field">
                  <input
                    className="dyna-modal__input"
                    type="text"
                    placeholder={t("dyna.niche_modal.industry_other_ph")}
                    value={industryOther}
                    onChange={(e) => setIndustryOther(e.target.value)}
                  />
                </div>
              )}

              {errorMsg && (
                <p style={{ color: "var(--destructive, #e26060)", fontSize: 14, margin: 0 }}>
                  {errorMsg}
                </p>
              )}

              <button
                className="dyna-btn dyna-btn--primary"
                type="submit"
                disabled={loading}
                style={{ width: "100%", justifyContent: "center", borderRadius: 12, padding: "16px 24px", fontSize: 15 }}
              >
                {loading ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 0.7s linear infinite" }}>
                      <circle cx="8" cy="8" r="6" stroke="rgba(253,248,240,0.3)" strokeWidth="2" />
                      <path d="M8 2a6 6 0 0 1 6 6" stroke="var(--quartz-200)" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    {t("dyna.niche_modal.sending")}
                  </span>
                ) : (
                  <span>{t("dyna.niche_modal.submit")} <span className="dyna-arrow">→</span></span>
                )}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
